/**
 * PropertySection 互換 slice — 全 thunk は /api/v1/units/... を呼び、
 * Unit レスポンスを `unitToPropertySection` で旧 PropertySection shape に変換して
 * 旧 UI に渡す互換アダプタ。Unit に投影できないフィールドは無視する。
 *
 * 書込 (create / update /書類削除) は Unit API の対応エンドポイントを順次呼ぶ。
 * 契約系 (新規 / 編集 / 更新 / 退去) は contractSlice の thunk
 * (createContract / updateContract / renewContract / endContract) を直接使う。
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import type {
  PropertySection,
  PropertySectionsState,
  PaginatedResponse,
  OtherFee,
} from "@/types/propertySection";
import type { Unit, AreaType, Contract } from "@ippon/shared-types";
import {
  unitToPropertySection,
  unitListToPropertySectionList,
  metaUpdateBodyToUnitBody,
  sectionCategoryToAreaType,
} from "@/utils/unitPropertySectionAdapter";
import { toApiErrorPayload } from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

// ---------------------------------------------------------------------------
// Thunks (Unit API 経由 + adapter)
// ---------------------------------------------------------------------------

export const createPropertySection = createAsyncThunk<
  PropertySection,
  Record<string, unknown>
>("propertySections/create", async (payload, { rejectWithValue }) => {
  try {
    const sectionCategory = (payload as any).section_category as
      | string
      | undefined;
    const areaType: AreaType = sectionCategory
      ? sectionCategoryToAreaType(sectionCategory as any)
      : "private";

    const unitBody: Record<string, unknown> = {
      property_id: payload.property_id,
      area_type: areaType,
      landmark_name: (payload as any).landmark_name,
      occupancy_status: (payload as any).occupancy_status,
      revenue_type: (payload as any).is_revenue_generating
        ? areaType === "private"
          ? "stock_rent"
          : "stock_other"
        : "none",
      ...metaUpdateBodyToUnitBody(payload as any),
    };
    // BE は body.contract が含まれていれば Contracts テーブルに発番する。
    // Unit 本体には埋め込まない (= 旧 unit.contract ContractInfo は使わない)。
    if ((payload as any).contract) {
      (unitBody as any).contract = (payload as any).contract;
    }
    const res = await ipponApi.post(`${BaseURL}/api/v1/units`, unitBody);
    return unitToPropertySection(res.data.data as Unit);
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data || "Failed to create unit",
    );
  }
});

export const fetchPropertySectionsByProperty = createAsyncThunk<
  PaginatedResponse<PropertySection> | PropertySection[],
  {
    property_id: string;
    /** 旧スキーマとの互換用パラメータ。Unit API では area_type に変換 */
    section_category?: string;
    page?: number;
    limit?: number;
  }
>(
  "propertySections/fetchByProperty",
  async ({ property_id, section_category }, { rejectWithValue }) => {
    try {
      const params: Record<string, unknown> = { property_id };
      if (section_category) {
        params.area_type = sectionCategoryToAreaType(section_category as any);
      }
      // 契約情報は Contracts テーブルが真値のため、active 契約を並列取得して
      // unit.current_contract_id (無ければ unit_id) で join する。
      const [unitsRes, contractsRes] = await Promise.all([
        ipponApi.get(`${BaseURL}/api/v1/units`, { params }),
        ipponApi.get(`${BaseURL}/api/v1/contracts`, {
          params: { property_id, status: "active" },
        }),
      ]);
      const items = (unitsRes.data?.data?.items || []) as Unit[];
      const activeContracts = (contractsRes.data?.data?.items || []) as Contract[];
      const contractsByUnitId = buildContractsByUnitIdMap(activeContracts);
      return unitListToPropertySectionList(items, contractsByUnitId);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch units",
      );
    }
  },
);

/**
 * unit_id → 最新 active 契約 のマップを作る。
 * 1 区画に複数 active が紛れている場合は created_at の新しい方を採用。
 */
function buildContractsByUnitIdMap(
  contracts: Contract[],
): Record<string, Contract | undefined> {
  const map: Record<string, Contract | undefined> = {};
  for (const c of contracts) {
    const uid = (c as any)?.unit_id as string | undefined;
    if (!uid) continue;
    const prev = map[uid];
    if (
      !prev ||
      new Date(c.created_at ?? 0).getTime() >
        new Date(prev.created_at ?? 0).getTime()
    ) {
      map[uid] = c;
    }
  }
  return map;
}

export const fetchPropertySectionById = createAsyncThunk<
  PropertySection,
  { property_section_id: string }
>(
  "propertySections/fetchById",
  async ({ property_section_id }, { rejectWithValue }) => {
    try {
      // property_section_id は Unit 移行後 unit_id と同義
      // 契約情報の真値は Contracts テーブル側にあるため active 契約も並列取得して
      // unit.current_contract_id (または unit_id) で join する。
      const [unitRes, contractsRes] = await Promise.all([
        ipponApi.get(`${BaseURL}/api/v1/units/${property_section_id}`),
        ipponApi.get(`${BaseURL}/api/v1/contracts`, {
          params: { unit_id: property_section_id, status: "active" },
        }),
      ]);
      const unit = unitRes.data.data as Unit;
      const contracts = (contractsRes.data?.data?.items || []) as Contract[];
      const activeContract = pickLatestActiveContract(
        contracts,
        (unit as any).current_contract_id,
      );
      return unitToPropertySection(unit, activeContract);
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "Failed to fetch unit"),
      );
    }
  },
);

/**
 * 同一 unit に複数 active 契約が紛れているケースに備えて、current_contract_id が
 * あればそれを最優先で採用し、無ければ created_at の新しいものを返す。
 */
function pickLatestActiveContract(
  contracts: Contract[],
  currentContractId?: string,
): Contract | null {
  if (contracts.length === 0) return null;
  if (currentContractId) {
    const hit = contracts.find((c) => c.contract_id === currentContractId);
    if (hit) return hit;
  }
  return [...contracts].sort(
    (a, b) =>
      new Date(b.created_at ?? 0).getTime() -
      new Date(a.created_at ?? 0).getTime(),
  )[0];
}

export const updateCommonAreaSection = createAsyncThunk<
  PropertySection,
  { property_section_id: string; body: Record<string, unknown> }
>(
  "propertySections/updateCommonArea",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/units/${property_section_id}`,
        metaUpdateBodyToUnitBody(body as any),
      );
      return unitToPropertySection(res.data.data as Unit);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update common area",
      );
    }
  },
);

export const updatePropertySectionMeta = createAsyncThunk<
  PropertySection,
  {
    property_section_id: string;
    body: {
      landmark_name?: string;
      remarks?: string;
      usage?: string;
      contract_form?: string;
      setup_type?: string;
      rental_area_m2?: number;
      rental_area_tsubo?: number;
      floor?: string;
      floor_plan?: string;
      initial_contract_start_date?: string;
      rent_start_date?: string;
      cancellation_date?: string;
      monthly_rent?: number;
      key_money?: number;
      maintenance_fee?: number;
      rent_per_tsubo?: number;
      maintenance_fee_per_tsubo?: number;
      total_rent?: number;
      total_rent_per_tsubo?: number;
      security_deposit_before?: number;
      security_deposit_after?: number;
      section_category?: string;
      area_type?: AreaType;
      parking_method?: string;
      other_fees?: OtherFee[];
      section_images?: Array<
        | string
        | {
            data?: string;
            originalFileName?: string;
            mimeType?: string;
          }
      > | null;
    };
  }
>(
  "propertySections/updateMeta",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/units/${property_section_id}`,
        metaUpdateBodyToUnitBody(body as any),
      );
      return unitToPropertySection(res.data.data as Unit);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update unit",
      );
    }
  },
);

/**
 * 書類追加 — Unit に documents サブオブジェクトとして集約。
 * 旧の category 別 (lease_contract / id_document 等) は category="contract" 等にマップ。
 */
export const updatePropertySectionDocuments = createAsyncThunk<
  PropertySection,
  {
    property_section_id: string;
    body: { contractor_type?: string; category: string; files: unknown[] };
  }
>(
  "propertySections/updateDocuments",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const documents = (body.files || []).map((f: any, idx: number) => ({
        document_id: `${Date.now()}-${idx}`,
        category: "contract" as const,
        file_name: f.file_name || f.originalFileName || `document-${idx}`,
        s3_key: f.s3_key || f.data || "",
        uploaded_at: new Date().toISOString(),
      }));
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/units/${property_section_id}`,
        { documents },
      );
      return unitToPropertySection(res.data.data as Unit);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update documents",
      );
    }
  },
);

export const deletePropertySectionDocument = createAsyncThunk<
  PropertySection,
  { property_section_id: string; s3_key: string; file_name?: string }
>(
  "propertySections/deleteDocument",
  async ({ property_section_id, s3_key }, { rejectWithValue }) => {
    try {
      // Unit を取得 → documents から該当を除外 → PATCH
      const get = await ipponApi.get(
        `${BaseURL}/api/v1/units/${property_section_id}`,
      );
      const unit = get.data.data as Unit;
      const filtered = (unit.documents || []).filter(
        (d) => d.s3_key !== s3_key,
      );
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/units/${property_section_id}`,
        { documents: filtered },
      );
      return unitToPropertySection(res.data.data as Unit);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to delete document",
      );
    }
  },
);

export const deletePropertySections = createAsyncThunk<
  string[],
  { property_section_ids: string[] }
>(
  "propertySections/delete",
  async ({ property_section_ids }, { rejectWithValue }) => {
    try {
      // BE はバルク削除 API を持たないので順次 DELETE
      await Promise.all(
        property_section_ids.map((id) =>
          ipponApi.delete(`${BaseURL}/api/v1/units/${id}`),
        ),
      );
      return property_section_ids;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to delete units",
      );
    }
  },
);

// ---------------------------------------------------------------------------
// State / Slice (旧来のまま — adapter 後のデータが流れる)
// ---------------------------------------------------------------------------

const initialState: PropertySectionsState = {
  creating: { loading: false, error: false, data: null },
  list: {
    loading: false,
    error: false,
    data: [],
    pagination: undefined,
  },
  current: { loading: false, error: false, data: null },
};

export const propertySectionsSlice = createSlice({
  name: "propertySections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createPropertySection.pending, (state) => {
      state.creating.loading = true;
      state.creating.error = false;
    });
    builder.addCase(createPropertySection.fulfilled, (state, action) => {
      state.creating.loading = false;
      state.creating.data = action.payload;
    });
    builder.addCase(createPropertySection.rejected, (state) => {
      state.creating.loading = false;
      state.creating.error = true;
    });

    builder.addCase(fetchPropertySectionsByProperty.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(
      fetchPropertySectionsByProperty.fulfilled,
      (state, action) => {
        state.list.loading = false;
        const payload = action.payload as any;
        if (payload?.data && payload?.pagination) {
          state.list.data = Array.isArray(payload.data) ? payload.data : [];
          state.list.pagination = payload.pagination;
        } else {
          state.list.data = Array.isArray(payload) ? payload : [];
          state.list.pagination = undefined;
        }
      },
    );
    builder.addCase(fetchPropertySectionsByProperty.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });

    builder.addCase(fetchPropertySectionById.pending, (state, action) => {
      const nextId = (action.meta.arg as any)?.property_section_id || null;
      const cur = state.current as any;
      if (cur.requestedId !== nextId) {
        state.current.data = null;
      }
      cur.requestedId = nextId;
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(fetchPropertySectionById.fulfilled, (state, action) => {
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(fetchPropertySectionById.rejected, (state, action) => {
      state.current.loading = false;
      state.current.error = (action.payload as any) || true;
    });

    builder.addCase(updateCommonAreaSection.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateCommonAreaSection.fulfilled, (state, action) => {
      state.current.loading = false;
      const updated = action.payload;
      if (updated) {
        state.current.data = updated;
        if (Array.isArray(state.list.data)) {
          state.list.data = state.list.data.map((item) =>
            String(item?.property_section_id) ===
            String(updated.property_section_id)
              ? updated
              : item,
          );
        }
      }
    });
    builder.addCase(updateCommonAreaSection.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(updatePropertySectionMeta.pending, (state) => {
      state.current.error = false;
    });
    builder.addCase(updatePropertySectionMeta.fulfilled, (state, action) => {
      const updated = action.payload;
      if (updated) {
        state.current.data = updated;
        if (Array.isArray(state.list.data)) {
          state.list.data = state.list.data.map((item) =>
            String(item?.property_section_id) ===
            String(updated.property_section_id)
              ? updated
              : item,
          );
        }
      }
    });
    builder.addCase(updatePropertySectionMeta.rejected, (state) => {
      state.current.error = true;
    });

    builder.addCase(updatePropertySectionDocuments.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(
      updatePropertySectionDocuments.fulfilled,
      (state, action) => {
        state.current.loading = false;
        const updated = action.payload;
        if (updated) state.current.data = updated;
      },
    );
    builder.addCase(updatePropertySectionDocuments.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(deletePropertySectionDocument.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(
      deletePropertySectionDocument.fulfilled,
      (state, action) => {
        state.current.loading = false;
        const updated = action.payload;
        if (updated) state.current.data = updated;
      },
    );
    builder.addCase(deletePropertySectionDocument.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(deletePropertySections.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(deletePropertySections.fulfilled, (state, action) => {
      state.list.loading = false;
      const deletedIds = new Set(action.payload);
      state.list.data = state.list.data.filter(
        (item) => !deletedIds.has(item.property_section_id),
      );
    });
    builder.addCase(deletePropertySections.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });
  },
});

export default propertySectionsSlice.reducer;
